import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  Badge, Button, Card, CardContent, Combobox, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/cargofl-ui';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { api } from '@/lib/api';
import { useVisiblePeople } from '@/lib/people-api';

interface AuditRow {
  id: string;
  action: string;
  entity: string;
  entityId: string;
  summary: string;
  createdAt: string;
  actor: { id: string; displayName: string };
  subject: { id: string; displayName: string } | null;
}

interface AuditPage {
  rows: AuditRow[];
  total: number;
  page: number;
  pageSize: number;
  actions: { action: string; count: number }[];
}

/**
 * Who changed something they do not own.
 *
 * The workflow already recorded its own decisions - every submit and approval, who
 * closed a month and why, who changed a setting. This is the ordinary half: who
 * edited that entry, who changed that person's role, who set that rate. It only
 * has rows where the actor and the owner differ, or where the thing changed is
 * shared, so an employee filling in their own timesheet never appears here.
 *
 * The trail is append-only by construction: nothing in the app writes to it except
 * the service that records, and there is deliberately no delete.
 */
export function AuditPage() {
  const [page, setPage] = React.useState(1);
  const [action, setAction] = React.useState<string | null>(null);
  const [subjectId, setSubjectId] = React.useState<string | null>(null);
  const { data: people } = useVisiblePeople();

  const query = new URLSearchParams({ page: String(page) });
  if (action) query.set('action', action);
  if (subjectId) query.set('subjectId', subjectId);

  const { data, isPending } = useQuery({
    queryKey: ['admin', 'audit', page, action, subjectId],
    queryFn: () => api.get<AuditPage>(`/admin/settings/audit?${query.toString()}`),
  });

  const lastPage = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  // Changing a filter has to go back to page one, or an empty page 4 reads as
  // "nothing matches" when it means "not this far in".
  const setFilter = (next: () => void) => {
    next();
    setPage(1);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Audit"
        description="Who changed something that was not their own, and when."
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Combobox
            value={action}
            onChange={(value) => setFilter(() => setAction(value))}
            options={(data?.actions ?? []).map((row) => ({
              value: row.action,
              label: row.action,
              description: `${row.count} event${row.count === 1 ? '' : 's'}`,
            }))}
            placeholder="Any action"
            searchPlaceholder="Search actions"
          />
        </div>
        <div className="w-56">
          <Combobox
            value={subjectId}
            onChange={(value) => setFilter(() => setSubjectId(value))}
            options={(people ?? []).map((person) => ({
              value: person.id,
              label: person.displayName,
            }))}
            placeholder="Anybody affected"
            searchPlaceholder="Search people"
          />
        </div>
        {(action || subjectId) && (
          <Button
            variant="ghost"
            onClick={() =>
              setFilter(() => {
                setAction(null);
                setSubjectId(null);
              })
            }
          >
            Clear
          </Button>
        )}

        <div className="flex-1" />

        {data && data.total > data.pageSize && (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              aria-label="Previous page"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="ts-num min-w-[6rem] text-center text-sm text-muted-foreground">
              {page} of {lastPage}
            </span>
            <Button
              variant="outline"
              size="icon"
              aria-label="Next page"
              disabled={page >= lastPage}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        )}
      </div>

      {isPending && <Skeleton className="h-96 w-full" />}

      {data && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table className="min-w-[820px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-44">When</TableHead>
                    <TableHead className="w-40">Who</TableHead>
                    <TableHead className="w-44">What</TableHead>
                    <TableHead>Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="ts-num whitespace-nowrap text-sm text-muted-foreground">
                        {when(row.createdAt)}
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {row.actor.displayName}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-[11px] font-normal">
                          {row.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {row.summary}
                        {/* Named separately as well as inside the summary: this is
                            the column somebody scans when the question is "what has
                            been done to this person". */}
                        {row.subject && row.subject.id !== row.actor.id && (
                          <span className="block text-xs text-muted-foreground">
                            affected {row.subject.displayName}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {data.rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                        {action || subjectId
                          ? 'Nothing matches that filter.'
                          : 'Nothing recorded yet. Rows appear here when somebody changes another person&rsquo;s time, a role, a rate or a contract.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {data && data.total > 0 && (
        <p className="text-xs text-muted-foreground">
          {data.total} event{data.total === 1 ? '' : 's'} recorded.
        </p>
      )}
    </PageContainer>
  );
}

/** Local time, because this is read by somebody sitting in the office. */
function when(iso: string): string {
  const date = new Date(iso);
  return `${date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })} ${date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}
